import React from 'react';
import { Severity, SEVERITY_CLASSES } from '../types';
import { PANEL, PANEL_HEADER, PANEL_TITLE, severityWord } from './panelChrome';

interface MetricCard {
  label: string;
  value: string | number;
  unit?: string;
  severity?: Severity;
}

interface MetricsCardsProps {
  metrics: MetricCard[];
}

const MetricsCards: React.FC<MetricsCardsProps> = ({ metrics }) => {
  if (!metrics || metrics.length === 0) return null;

  return (
    <section className={PANEL}>
      <div className={PANEL_HEADER}>
        <h3 className={PANEL_TITLE}>Flight metrics</h3>
      </div>
      <div className="grid grid-cols-[repeat(auto-fill,minmax(9.5rem,1fr))] gap-1.5 p-3">
        {metrics.map((m, i) => {
          const flagged = m.severity && m.severity !== 'info' ? SEVERITY_CLASSES[m.severity] : null;
          return (
            <div
              key={i}
              className={`min-w-0 rounded bg-surface-container px-2.5 py-2 ${flagged ? `ring-1 ring-inset ${flagged.ring}` : ''}`}
            >
              <div className="mb-1 flex min-w-0 items-baseline justify-between gap-2 text-xs">
                <span className="truncate text-on-surface-variant">{m.label}</span>
                {flagged && m.severity && (
                  <span className={`shrink-0 font-semibold ${flagged.text}`}>{severityWord(m.severity)}</span>
                )}
              </div>
              <div
                className={`truncate font-mono text-base font-semibold tabular-nums ${flagged ? flagged.text : 'text-on-surface'}`}
              >
                {m.value}
                {m.unit && (
                  <span className="ml-1 text-xs font-normal text-on-surface-variant">{m.unit}</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default MetricsCards;

export const extractMetrics = (data: any): MetricCard[] => {
  const metrics: MetricCard[] = [];

  if (data?.drone_serial) metrics.push({ label: 'Serial', value: data.drone_serial });
  if (data?.firmware_branch) metrics.push({ label: 'Firmware', value: data.firmware_branch });
  if (data?.duration_s) metrics.push({ label: 'Duration', value: `${Math.floor(data.duration_s / 60)}:${String(Math.floor(data.duration_s % 60)).padStart(2, '0')}`, unit: 'mm:ss' });

  const summary = data?.per_category?.flight_summary?.metrics;
  if (summary) {
    if (summary.hardware) metrics.push({ label: 'Hardware', value: summary.hardware });
    if (summary.topic_count) metrics.push({ label: 'Topics', value: summary.topic_count });
    if (summary.param_count) metrics.push({ label: 'Parameters', value: summary.param_count });
  }

  const gps = data?.per_category?.ekf_navigation?.metrics?.gps_quality;
  if (gps) {
    if (gps.fix_type_name) metrics.push({ label: 'GPS fix', value: gps.fix_type_name });
    if (gps.satellites_mean) metrics.push({ label: 'Satellites', value: Math.round(gps.satellites_mean) });
  }

  const batt = data?.per_category?.battery?.metrics?.per_battery?.[0];
  if (batt) {
    if (batt.voltage_sag_v) metrics.push({ label: 'Voltage sag', value: batt.voltage_sag_v, unit: 'V', severity: batt.voltage_sag_v > 1.5 ? 'warning' : 'info' });
    if (batt.remaining_pct_start != null) metrics.push({ label: 'Battery at start', value: `${batt.remaining_pct_start}%` });
    if (batt.remaining_pct_end != null) metrics.push({ label: 'Battery at end', value: `${batt.remaining_pct_end}%` });
  }

  const vib = data?.per_category?.vibration?.metrics;
  if (vib) {
    if (vib.primary_metric != null) metrics.push({
      label: 'Vibration RMS',
      value: vib.primary_metric,
      unit: 'm/s\u00B2',
      severity: vib.primary_metric >= 9.81 ? 'critical' : vib.primary_metric >= 4.905 ? 'warning' : 'info',
    });
    if (vib.total_clipping != null) metrics.push({
      label: 'Clipping',
      value: vib.total_clipping,
      severity: vib.total_clipping > 0 ? 'critical' : 'info',
    });
  }

  const esc = data?.per_category?.motor_esc?.metrics;
  if (esc) {
    if (esc.rpm_balance?.rpm_spread_pct != null) metrics.push({
      label: 'RPM spread',
      value: `${esc.rpm_balance.rpm_spread_pct}%`,
      severity: esc.rpm_balance.rpm_spread_pct > 10 ? 'warning' : 'info',
    });
  }

  return metrics;
};
